import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Offcanvas,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Activity,
  CheckCircle,
  Edit2,
  PlusCircle,
  Trash2,
  XCircle,
} from "react-feather";
import { useGet, usePatch, usePost } from "../../api";
import {
  IFullAutoCouponsTargetingSDKTest,
  IFullAutoCouponsTargetingSDKTestsResponse,
  ISdkTestBasketTest,
  ISdkTestDeviceLanguageCode,
  ITesterProfile,
  SDK_TEST_FRESH_DAYS,
  SDK_TEST_SAMPLE_APPS,
  SDK_TEST_CHECKOUT_TIMINGS,
  SDK_TEST_LANGUAGES,
  SdkTestSampleApp,
  SdkTestCheckoutTiming,
  SdkTestLanguage,
  sdkTestSampleAppLabel,
  sdkTestCheckoutTimingLabel,
  sdkTestLanguageLabel,
  testerRoleLabel,
} from "../../interfaces";
import { TesterProfileModal } from "./tester-profile-modal";

// Remember the user's last sample-app choice across sessions (per browser).
const SAMPLE_APP_STORAGE_KEY = "sdk_test_sample_app";
// Remember which saved device profile was last used to test with.
const SELECTED_PROFILE_STORAGE_KEY = "sdk_test_profile_id";

const readStoredSampleApp = (): SdkTestSampleApp => {
  try {
    const stored = window.localStorage.getItem(SAMPLE_APP_STORAGE_KEY);
    if (stored && SDK_TEST_SAMPLE_APPS.some((a) => a.value === stored)) {
      return stored as SdkTestSampleApp;
    }
  } catch {
    // localStorage unavailable — fall through to default.
  }
  return SDK_TEST_SAMPLE_APPS[0].value;
};

const DEFAULT_TIMING: SdkTestCheckoutTiming = "lt2";
const DEFAULT_LANGUAGE: SdkTestLanguage = SDK_TEST_LANGUAGES[0].value;

/** Small reusable pass/fail radio pair. */
const ResultRadios: React.FC<{
  name: string;
  value: "pass" | "fail";
  onChange: (v: "pass" | "fail") => void;
}> = ({ name, value, onChange }) => (
  <div className="d-flex gap-3">
    <Form.Check
      type="radio"
      id={`${name}-pass`}
      name={name}
      label={
        <span className="text-success fw-semibold">
          <CheckCircle size={13} className="me-1" />
          Pass
        </span>
      }
      checked={value === "pass"}
      onChange={() => onChange("pass")}
    />
    <Form.Check
      type="radio"
      id={`${name}-fail`}
      name={name}
      label={
        <span className="text-danger fw-semibold">
          <XCircle size={13} className="me-1" />
          Fail
        </span>
      }
      checked={value === "fail"}
      onChange={() => onChange("fail")}
    />
  </div>
);

const TimingSelect: React.FC<{
  value: SdkTestCheckoutTiming;
  onChange: (v: SdkTestCheckoutTiming) => void;
}> = ({ value, onChange }) => (
  <Form.Select
    size="sm"
    value={value}
    onChange={(e) => onChange(e.target.value as SdkTestCheckoutTiming)}
  >
    {SDK_TEST_CHECKOUT_TIMINGS.map((t) => (
      <option key={t.value} value={t.value}>
        {t.label}
      </option>
    ))}
  </Form.Select>
);

const ResultBadge: React.FC<{ result: "pass" | "fail" }> = ({ result }) =>
  result === "pass" ? (
    <Badge bg="success">Pass</Badge>
  ) : (
    <Badge bg="danger">Fail</Badge>
  );

/** `allowed` narrows the picker to the languages recorded against this
 *  domain; empty means the domain has none on record, so show them all. */
const LanguageSelect: React.FC<{
  value: SdkTestLanguage;
  onChange: (v: SdkTestLanguage) => void;
  allowed?: SdkTestLanguage[];
}> = ({ value, onChange, allowed }) => {
  const options = allowed?.length
    ? SDK_TEST_LANGUAGES.filter((l) => allowed.includes(l.value))
    : SDK_TEST_LANGUAGES;
  return (
    <Form.Select
      size="sm"
      value={value}
      onChange={(e) => onChange(e.target.value as SdkTestLanguage)}
    >
      {options.map((l) => (
        <option key={l.value} value={l.value}>
          {l.label}
        </option>
      ))}
    </Form.Select>
  );
};

/** Result + timing for both checkouts of one basket shape. Single basket,
 *  multi basket and the translation run all take this shape. */
const useBasketTestState = () => {
  const [firstResult, setFirstResult] = useState<"pass" | "fail">("pass");
  const [firstTiming, setFirstTiming] =
    useState<SdkTestCheckoutTiming>(DEFAULT_TIMING);
  const [secondResult, setSecondResult] = useState<"pass" | "fail">("pass");
  const [secondTiming, setSecondTiming] =
    useState<SdkTestCheckoutTiming>(DEFAULT_TIMING);

  return {
    firstResult,
    setFirstResult,
    firstTiming,
    setFirstTiming,
    secondResult,
    setSecondResult,
    secondTiming,
    setSecondTiming,
    passed: firstResult === "pass" && secondResult === "pass",
    reset: () => {
      setFirstResult("pass");
      setFirstTiming(DEFAULT_TIMING);
      setSecondResult("pass");
      setSecondTiming(DEFAULT_TIMING);
    },
    toPayload: (): ISdkTestBasketTest => ({
      firstCheckoutResult: firstResult,
      firstCheckoutTiming: firstTiming,
      secondCheckoutResult: secondResult,
      secondCheckoutTiming: secondTiming,
    }),
  };
};

type BasketTestState = ReturnType<typeof useBasketTestState>;

/** The two checkout blocks for one basket shape, side by side. */
const BasketTestRow: React.FC<{ name: string; state: BasketTestState }> = ({
  name,
  state,
}) => (
  <Row className="g-3 mb-4">
    <Col md={6}>
      <CheckoutTestBlock
        title="First checkout test"
        name={`${name}-first`}
        result={state.firstResult}
        onResultChange={state.setFirstResult}
        timing={state.firstTiming}
        onTimingChange={state.setFirstTiming}
      />
    </Col>
    <Col md={6}>
      <CheckoutTestBlock
        title="Second checkout test"
        name={`${name}-second`}
        result={state.secondResult}
        onResultChange={state.setSecondResult}
        timing={state.secondTiming}
        onTimingChange={state.setSecondTiming}
      />
    </Col>
  </Row>
);

/** One checkout-test block: result radios + timing select, used by both the
 *  single-basket and translation-test sections. */
const CheckoutTestBlock: React.FC<{
  title: string;
  result: "pass" | "fail";
  onResultChange: (v: "pass" | "fail") => void;
  timing: SdkTestCheckoutTiming;
  onTimingChange: (v: SdkTestCheckoutTiming) => void;
  name: string;
}> = ({ title, result, onResultChange, timing, onTimingChange, name }) => (
  <div className="border rounded p-2 h-100">
    <p className="small fw-semibold mb-2">{title}</p>
    <Form.Label className="small mb-1">Result</Form.Label>
    <ResultRadios name={name} value={result} onChange={onResultChange} />
    <Form.Label className="small mb-1 mt-2">Time to complete</Form.Label>
    <TimingSelect value={timing} onChange={onTimingChange} />
  </div>
);

export const SDKTestModal: React.FC<{
  show: boolean;
  itemId: string;
  domain: string;
  countryCode: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ show, itemId, domain, countryCode, onClose, onSaved }) => {
  const [sampleApp, setSampleApp] =
    useState<SdkTestSampleApp>(readStoredSampleApp);
  const single = useBasketTestState();
  const multi = useBasketTestState();

  // Translation test — only shown when the domain requires it.
  const [translationLanguage, setTranslationLanguage] =
    useState<SdkTestLanguage>(DEFAULT_LANGUAGE);
  const translation = useBasketTestState();

  // Device language check — device's own language, no checkout involved.
  const [deviceLanguage, setDeviceLanguage] =
    useState<SdkTestLanguage>(DEFAULT_LANGUAGE);
  const [deviceCodes, setDeviceCodes] = useState<ISdkTestDeviceLanguageCode[]>(
    []
  );
  const [newCode, setNewCode] = useState("");

  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
    () => {
      try {
        return window.localStorage.getItem(SELECTED_PROFILE_STORAGE_KEY);
      } catch {
        return null;
      }
    }
  );
  const [editingProfile, setEditingProfile] = useState<
    ITesterProfile | null | "new"
  >(null);

  const { data: profilesData, refetch: refetchProfiles } = useGet<{
    profiles: ITesterProfile[];
  }>(show ? "tester-profile" : (null as any));
  const profiles = profilesData?.profiles ?? [];

  // Default to the last-used device, falling back to the first saved one.
  useEffect(() => {
    if (!profiles.length) return;
    if (!profiles.some((p) => p._id === selectedProfileId)) {
      setSelectedProfileId(profiles[0]._id);
    }
  }, [profiles, selectedProfileId]);

  useEffect(() => {
    if (!selectedProfileId) return;
    try {
      window.localStorage.setItem(
        SELECTED_PROFILE_STORAGE_KEY,
        selectedProfileId
      );
    } catch {
      // ignore storage failures
    }
  }, [selectedProfileId]);

  const selectedProfile =
    profiles.find((p) => p._id === selectedProfileId) ?? null;

  // Derived: every single- and multi-basket checkout must pass. Translation
  // is recorded but deliberately doesn't gate — a localisation bug shouldn't
  // hold the brand out of Complete.
  const overallResult: "pass" | "fail" =
    single.passed && multi.passed ? "pass" : "fail";

  const { execute: submitTest } = usePost<IFullAutoCouponsTargetingSDKTest>();

  const { data: historyData, loading: historyLoading } =
    useGet<IFullAutoCouponsTargetingSDKTestsResponse>(
      show && itemId
        ? `full-auto-coupons-targeting/items/${itemId}/sdk-tests?limit=20`
        : (null as any)
    );

  // Which languages this storefront can be translated into is a property of
  // the domain, not of one test run — recorded once here, then every tester
  // picks from it.
  const { execute: saveSettings, loading: savingSettings } = usePatch();
  const [canTranslate, setCanTranslate] = useState(false);
  const [siteLanguages, setSiteLanguages] = useState<SdkTestLanguage[]>([]);

  useEffect(() => {
    if (!historyData) return;
    setCanTranslate(historyData.requiresTranslationTest);
    setSiteLanguages(historyData.translationLanguages ?? []);
  }, [historyData]);

  const requiresTranslationTest = canTranslate;

  // Keep the tester's pick inside what the domain actually offers.
  useEffect(() => {
    if (siteLanguages.length && !siteLanguages.includes(translationLanguage)) {
      setTranslationLanguage(siteLanguages[0]);
    }
  }, [siteLanguages, translationLanguage]);

  const persistTranslationSettings = async (
    next: Partial<{
      requiresTranslationTest: boolean;
      translationLanguages: SdkTestLanguage[];
    }>
  ) => {
    if (!itemId) return;
    try {
      await saveSettings(
        `full-auto-coupons-targeting/items/${itemId}`,
        next as any
      );
    } catch (e: any) {
      setSaveError(
        e?.data?.error ?? e?.message ?? "Failed to save translation settings."
      );
    }
  };

  const toggleSiteLanguage = (lang: SdkTestLanguage) => {
    const next = siteLanguages.includes(lang)
      ? siteLanguages.filter((l) => l !== lang)
      : [...siteLanguages, lang];
    setSiteLanguages(next);
    persistTranslationSettings({ translationLanguages: next });
  };

  const resetForm = () => {
    single.reset();
    multi.reset();
    translation.reset();
    setTranslationLanguage(DEFAULT_LANGUAGE);
    setDeviceLanguage(DEFAULT_LANGUAGE);
    setDeviceCodes([]);
    setNewCode("");
    setReason("");
    setSaveError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const addDeviceCode = () => {
    const code = newCode.trim();
    if (!code) return;
    setDeviceCodes((prev) => [...prev, { code, passed: true }]);
    setNewCode("");
  };

  const removeDeviceCode = (index: number) => {
    setDeviceCodes((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleDeviceCode = (index: number) => {
    setDeviceCodes((prev) =>
      prev.map((c, i) => (i === index ? { ...c, passed: !c.passed } : c))
    );
  };

  const handleSave = async () => {
    if (!itemId) return;
    setSaving(true);
    setSaveError(null);
    try {
      await submitTest(
        `full-auto-coupons-targeting/items/${itemId}/sdk-tests`,
        {
          sampleApp,
          ...single.toPayload(),
          multiBasketTest: multi.toPayload(),
          reason: reason.trim(),
          profileId: selectedProfileId,
          translationTest: requiresTranslationTest
            ? {
                languageTested: translationLanguage,
                ...translation.toPayload(),
              }
            : undefined,
          deviceLanguageCheck:
            deviceCodes.length > 0
              ? { deviceLanguage, codes: deviceCodes }
              : undefined,
        }
      );
      // Remember the chosen sample app for next time.
      try {
        window.localStorage.setItem(SAMPLE_APP_STORAGE_KEY, sampleApp);
      } catch {
        // ignore storage failures
      }
      onSaved();
      handleClose();
    } catch (e: any) {
      setSaveError(e?.data?.error ?? e?.message ?? "Failed to save test.");
    } finally {
      setSaving(false);
    }
  };

  const testingAsLabel = useMemo(() => {
    if (!selectedProfile) return null;
    const bits = [testerRoleLabel(selectedProfile.role)];
    if (selectedProfile.device) bits.push(selectedProfile.device);
    return bits.join(" · ");
  }, [selectedProfile]);

  return (
    <>
      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="end"
        style={{ width: "min(1040px, 96vw)" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fs-6">
            <Activity size={15} className="me-2" />
            APK Test — {domain} / {countryCode}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column">
          <Alert variant="light" className="border small py-2 mb-3">
            <div className="d-flex align-items-center justify-content-between gap-2">
              <span className="text-muted">Testing on device</span>
              <Button
                size="sm"
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => setEditingProfile("new")}
              >
                + add device
              </Button>
            </div>
            {profiles.length > 0 ? (
              <div className="d-flex align-items-center gap-2 mt-1">
                <Form.Select
                  size="sm"
                  value={selectedProfileId ?? ""}
                  onChange={(e) => setSelectedProfileId(e.target.value)}
                >
                  {profiles.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.label} — {testerRoleLabel(p.role)}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 text-decoration-none text-nowrap"
                  disabled={!selectedProfile}
                  onClick={() =>
                    selectedProfile && setEditingProfile(selectedProfile)
                  }
                >
                  <Edit2 size={12} className="me-1" />
                  edit
                </Button>
              </div>
            ) : (
              <div className="mt-1">
                <span className="text-muted small">
                  No device profiles set up yet —{" "}
                </span>
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 text-decoration-none"
                  onClick={() => setEditingProfile("new")}
                >
                  set one up
                </Button>
              </div>
            )}
            {selectedProfile && testingAsLabel && (
              <div className="small text-muted mt-1">{testingAsLabel}</div>
            )}
          </Alert>

          <p className="small text-muted mb-3">
            The overall test passes only when{" "}
            <strong>every single- and multi-basket checkout</strong> passes.
            Translation results are recorded but do not change it. A passing
            test within the last {SDK_TEST_FRESH_DAYS} days is required for the
            snapshot to be green.
          </p>

          <Row className="g-3 mb-3">
            <Col md={6} className="d-flex align-items-end">
              <span className="small text-muted">
                Overall result: <ResultBadge result={overallResult} />
              </span>
            </Col>
          </Row>

          <p className="small fw-semibold mb-1">Single Basket</p>
          <p className="small text-muted mb-2">
            One item in the basket. Required for every domain.
          </p>
          <BasketTestRow name="sdk-single" state={single} />

          <p className="small fw-semibold mb-1">Multi Basket</p>
          <p className="small text-muted mb-2">
            2× of one item plus 1× of a second item. Required for every domain.
          </p>
          <BasketTestRow name="sdk-multi" state={multi} />

          <div className="border rounded p-2 mb-3 bg-light">
            <Form.Check
              type="checkbox"
              id="sdk-can-translate"
              className="small fw-semibold"
              label="This site can be translated"
              checked={canTranslate}
              disabled={savingSettings}
              onChange={(e) => {
                setCanTranslate(e.target.checked);
                persistTranslationSettings({
                  requiresTranslationTest: e.target.checked,
                });
              }}
            />
            <p className="small text-muted mb-1">
              Saved against {domain} / {countryCode}, not against this run —
              every tester sees the same list.
            </p>
            {canTranslate && (
              <>
                <div className="d-flex flex-wrap gap-3">
                  {SDK_TEST_LANGUAGES.map((l) => (
                    <Form.Check
                      key={l.value}
                      type="checkbox"
                      id={`sdk-site-lang-${l.value}`}
                      className="small"
                      label={l.label}
                      checked={siteLanguages.includes(l.value)}
                      disabled={savingSettings}
                      onChange={() => toggleSiteLanguage(l.value)}
                    />
                  ))}
                </div>
                {siteLanguages.length === 0 && (
                  <p className="small text-warning mb-0 mt-1">
                    No languages recorded yet — tick the ones this site can be
                    translated into.
                  </p>
                )}
              </>
            )}
          </div>

          {requiresTranslationTest && (
            <>
              <p className="small fw-semibold mb-1">Translation Test</p>
              <p className="small text-muted mb-2">
                Record which language the site was translated to for this run.
                Recorded for the record — translation results do not change the
                overall pass/fail.
              </p>
              <Form.Group className="mb-2" style={{ maxWidth: 260 }}>
                <Form.Label className="small mb-1">
                  Site translated to
                </Form.Label>
                <LanguageSelect
                  value={translationLanguage}
                  onChange={setTranslationLanguage}
                  allowed={siteLanguages}
                />
              </Form.Group>
              <BasketTestRow name="sdk-translation" state={translation} />

              <p className="small fw-semibold mb-1">Device Language Check</p>
              <p className="small text-muted mb-2">
                Change the device's own language and look at the code
                descriptions — no checkout needed here. Record every code you
                saw and whether it translated correctly.
              </p>
              <Form.Group className="mb-2" style={{ maxWidth: 260 }}>
                <Form.Label className="small mb-1">
                  Device language set to
                </Form.Label>
                <LanguageSelect
                  value={deviceLanguage}
                  onChange={setDeviceLanguage}
                  allowed={siteLanguages}
                />
              </Form.Group>
              <div className="mb-2">
                {deviceCodes.map((c, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-2">
                    <span
                      className="small fw-semibold"
                      style={{ minWidth: 100 }}
                    >
                      {c.code}
                    </span>
                    <Form.Check
                      type="radio"
                      id={`device-code-${i}-pass`}
                      name={`device-code-${i}`}
                      label="Pass"
                      checked={c.passed}
                      onChange={() => toggleDeviceCode(i)}
                    />
                    <Form.Check
                      type="radio"
                      id={`device-code-${i}-fail`}
                      name={`device-code-${i}`}
                      label="Fail"
                      checked={!c.passed}
                      onChange={() => toggleDeviceCode(i)}
                    />
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 text-danger"
                      onClick={() => removeDeviceCode(i)}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                ))}
                <div className="d-flex gap-2">
                  <Form.Control
                    size="sm"
                    style={{ maxWidth: 200 }}
                    placeholder="e.g. SAVE10"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addDeviceCode();
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={addDeviceCode}
                  >
                    <PlusCircle size={13} className="me-1" /> Add code
                  </Button>
                </div>
              </div>
              <hr className="my-3" />
            </>
          )}

          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Label className="small mb-1 fw-semibold">
                Sample app tested
              </Form.Label>
              <Form.Select
                size="sm"
                value={sampleApp}
                onChange={(e) =>
                  setSampleApp(e.target.value as SdkTestSampleApp)
                }
              >
                {SDK_TEST_SAMPLE_APPS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={12}>
              <Form.Label className="small mb-1 fw-semibold">
                Reason / notes <span className="text-muted">(optional)</span>
              </Form.Label>
              <Form.Control
                size="sm"
                as="textarea"
                rows={2}
                placeholder="Describe what was tested and why it passed or failed…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
            </Col>
          </Row>

          {saveError && (
            <Alert variant="danger" className="small py-2">
              {saveError}
            </Alert>
          )}

          <div className="d-flex justify-content-end gap-2 mb-3">
            <Button size="sm" variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button
              size="sm"
              variant={overallResult === "pass" ? "success" : "danger"}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Activity size={13} className="me-1" />
              )}
              Record {overallResult === "pass" ? "Pass" : "Fail"}
            </Button>
          </div>

          <hr className="my-2" />
          <p className="small fw-semibold mb-2">Test history</p>
          {historyLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (historyData?.tests ?? []).length === 0 ? (
            <p className="small text-muted">No tests recorded yet.</p>
          ) : (
            <div style={{ maxHeight: 320, overflow: "auto" }}>
              <Table size="sm" className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>Overall</th>
                    <th>App</th>
                    <th>Single (1st/2nd)</th>
                    <th>Multi (1st/2nd)</th>
                    <th>Translation</th>
                    <th>Device Lang Check</th>
                    <th>Reason</th>
                    <th>Tested by</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(historyData?.tests ?? []).map((t) => (
                    <tr key={t.id}>
                      <td>
                        <ResultBadge result={t.result} />
                      </td>
                      <td className="text-nowrap">
                        {sdkTestSampleAppLabel(t.sampleApp)}
                      </td>
                      <td className="text-nowrap">
                        <ResultBadge result={t.firstCheckoutResult} />{" "}
                        <span className="text-muted">
                          {sdkTestCheckoutTimingLabel(t.firstCheckoutTiming)}
                        </span>{" "}
                        / <ResultBadge result={t.secondCheckoutResult} />{" "}
                        <span className="text-muted">
                          {sdkTestCheckoutTimingLabel(t.secondCheckoutTiming)}
                        </span>
                      </td>
                      <td className="text-nowrap">
                        {t.multiBasketTest ? (
                          <>
                            <ResultBadge
                              result={t.multiBasketTest.firstCheckoutResult}
                            />{" "}
                            <span className="text-muted">
                              {sdkTestCheckoutTimingLabel(
                                t.multiBasketTest.firstCheckoutTiming
                              )}
                            </span>{" "}
                            /{" "}
                            <ResultBadge
                              result={t.multiBasketTest.secondCheckoutResult}
                            />{" "}
                            <span className="text-muted">
                              {sdkTestCheckoutTimingLabel(
                                t.multiBasketTest.secondCheckoutTiming
                              )}
                            </span>
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-nowrap">
                        {t.translationTest ? (
                          <>
                            {sdkTestLanguageLabel(
                              t.translationTest.languageTested
                            )}{" "}
                            <ResultBadge
                              result={
                                t.translationTest.firstCheckoutResult ===
                                  "pass" &&
                                t.translationTest.secondCheckoutResult ===
                                  "pass"
                                  ? "pass"
                                  : "fail"
                              }
                            />
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-nowrap">
                        {t.deviceLanguageCheck ? (
                          <>
                            {sdkTestLanguageLabel(
                              t.deviceLanguageCheck.deviceLanguage
                            )}{" "}
                            <span className="text-muted">
                              (
                              {
                                t.deviceLanguageCheck.codes.filter(
                                  (c) => c.passed
                                ).length
                              }
                              /{t.deviceLanguageCheck.codes.length} pass)
                            </span>
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td style={{ maxWidth: 160, wordBreak: "break-word" }}>
                        {t.reason || "—"}
                      </td>
                      <td className="text-muted">
                        {t.testedBy ?? "—"}
                        {t.tester?.label && (
                          <span
                            className="text-muted"
                            title={`${t.tester.device} · ${t.tester.osVersion}`}
                          >
                            {" "}
                            ({t.tester.label})
                          </span>
                        )}
                      </td>
                      <td className="text-muted text-nowrap">
                        {new Date(t.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <TesterProfileModal
        show={editingProfile !== null}
        profile={editingProfile === "new" ? null : editingProfile}
        onClose={() => setEditingProfile(null)}
        onSaved={(p) => {
          setSelectedProfileId(p._id);
          refetchProfiles().catch(() => undefined);
        }}
        onDeleted={() => {
          setSelectedProfileId(null);
          refetchProfiles().catch(() => undefined);
        }}
      />
    </>
  );
};
